'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Route,
  X,
  MousePointer2,
  ExternalLink,
  Copy,
  Eye,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useProjectGraph } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import type { GraphNode as APIGraphNode, GraphEdge as APIGraphEdge } from '@/lib/api/types';

interface ProjectGraphReagraphProps {
  projectId: string;
  projectName?: string;
  depth?: number;
  includeConversations?: boolean;
}

// Node colors by type - slightly more vibrant
const NODE_COLORS: Record<string, string> = {
  project: '#4A90D9',
  rfq: '#F5A623',
  po: '#7ED321',
  vendor: '#BD10E0',
  vendor_response: '#50E3C2',
  lineitem: '#F8E71C',
  product: '#4DD0E1',
  message: '#9B9B9B',
  customer: '#E91E63',
  conversation: '#FF6B6B',  // Coral for agent conversations
  simulation: '#FF6B6B',    // Same as conversation
  mcp: '#F59E0B',           // Amber for cost plans
};

// Node sizes by type
const NODE_SIZES: Record<string, number> = {
  project: 14,
  rfq: 10,
  po: 10,
  vendor: 12,
  customer: 10,
  vendor_response: 8,
  lineitem: 6,
  product: 6,
  message: 5,
  conversation: 10,  // Medium size for conversations
  simulation: 10,
  mcp: 10,           // Same level as RFQ/PO
};

// Edge colors by relationship type
const EDGE_COLORS: Record<string, string> = {
  HAS_RFQ: '#F5A623',
  HAS_PO: '#7ED321',
  SENT_TO: '#BD10E0',
  RESPONDED_TO: '#50E3C2',
  CONTAINS: '#9B9B9B',
  SUPPLIES: '#4DD0E1',
  CREATED_BY: '#E91E63',
  HAS_CONVERSATION: '#FF6B6B',  // Links to conversation nodes
  ANALYZED: '#FF6B6B',          // Agent analyzed entity
  default: '#666666',
};

export function ProjectGraphReagraph({ projectId, projectName, depth = 2, includeConversations = false }: ProjectGraphReagraphProps) {
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const [useSelection, setUseSelection] = useState<any>(null);
  const { resolvedTheme } = useTheme();
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const graphRef = useRef<any>(null);

  // Selection and interaction state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);

  // Path finding state
  const [pathFindingMode, setPathFindingMode] = useState(false);
  const [pathStart, setPathStart] = useState<string | null>(null);
  const [pathEnd, setPathEnd] = useState<string | null>(null);
  const [pathNodes, setPathNodes] = useState<string[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: any;
  } | null>(null);

  // Clustering toggle
  const [clusteringEnabled, setClusteringEnabled] = useState(false);

  const projectGraphStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'project_visualize'));
  const { data, isLoading, error } = useProjectGraph(projectGraphStrategy!, {
    project_id: projectId,
    depth,
    include_messages: false,
    include_conversations: includeConversations,
  });

  // ObeyaGraphCard's own useCardExport call only knows projectId — it
  // never sees `data`, so it can't declare the query_key that would let
  // this card's actual content (the nodes/edges being painted) land in a
  // captured scene's `payloads`. Same gap as CostPlanGraphReagraph's,
  // fixed the same way: register here, next to where `data` materializes,
  // with a query_key matching useProjectGraph's own key exactly (hooks.ts)
  // — a mismatch means getObservedPayloads' cache lookup silently misses.
  const { contentKey } = useCardIdentity();
  useCardExport(useMemo(() => {
    const nodeTypeCounts: Record<string, number> = {};
    for (const n of data?.nodes ?? []) {
      nodeTypeCounts[n.type] = (nodeTypeCounts[n.type] ?? 0) + 1;
    }
    const status: 'loading' | 'error' | 'loaded' = isLoading ? 'loading' : error ? 'error' : 'loaded';
    return {
      content_key: contentKey ?? 'obeya_graph',
      status,
      error: error ? String((error as Error)?.message ?? error) : undefined,
      query_key: ['project-graph', projectGraphStrategy, projectId, depth, includeConversations],
      text: `Project graph: ${data?.nodes.length ?? 0} nodes, ${data?.edges.length ?? 0} edges`,
      json: data
        ? { node_count: data.nodes.length, edge_count: data.edges.length, node_types: nodeTypeCounts }
        : null,
    };
  }, [contentKey, data, isLoading, error, projectGraphStrategy, projectId, depth, includeConversations]));

  // Load ReaGraph only on client
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);
      setUseSelection(() => mod.useSelection);

      // Customize themes
      const customDarkTheme = {
        ...mod.darkTheme,
        canvas: {
          ...mod.darkTheme.canvas,
          background: '#121212',
        },
        node: {
          ...mod.darkTheme.node,
          activeFill: '#ffffff',
          inactiveOpacity: 0.3,
        },
        edge: {
          ...mod.darkTheme.edge,
          activeFill: '#ffffff',
          inactiveOpacity: 0.2,
        },
        cluster: {
          ...mod.darkTheme.cluster,
          stroke: '#ffffff22',
          fill: '#ffffff11',
          label: {
            stroke: '#ffffff',
            color: '#ffffff',
          },
        },
      };
      const customLightTheme = {
        ...mod.lightTheme,
        canvas: {
          ...mod.lightTheme.canvas,
          background: '#ffffff',
        },
        node: {
          ...mod.lightTheme.node,
          activeFill: '#000000',
          inactiveOpacity: 0.3,
        },
        edge: {
          ...mod.lightTheme.edge,
          activeFill: '#000000',
          inactiveOpacity: 0.2,
        },
        cluster: {
          ...mod.lightTheme.cluster,
          stroke: '#00000022',
          fill: '#00000011',
          label: {
            stroke: '#000000',
            color: '#000000',
          },
        },
      };
      setThemes({ dark: customDarkTheme, light: customLightTheme });
    });
  }, []);

  // Transform API data to ReaGraph format
  const { nodes, edges } = useMemo(() => {
    if (!data?.nodes) return { nodes: [], edges: [] };

    const nodes = data.nodes.map((node: APIGraphNode) => ({
      id: node.id,
      label: node.label,
      fill: NODE_COLORS[node.type] || '#8AB4F8',
      size: NODE_SIZES[node.type] || 6,
      // Cluster by node type
      cluster: node.type,
      // Pass all node data for display
      data: {
        type: node.type,
        status: node.status,
        odoo_id: node.odoo_id,
        ...node.extra,
      },
    }));

    const edges = data.edges.map((edge: APIGraphEdge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.type?.replace(/_/g, ' ') || '',
      fill: EDGE_COLORS[edge.type || ''] || EDGE_COLORS.default,
      size: edge.type === 'HAS_RFQ' || edge.type === 'HAS_PO' ? 2 : 1,
    }));

    return { nodes, edges };
  }, [data]);

  // Build adjacency list for path finding
  const adjacencyList = useMemo(() => {
    const adj: Record<string, string[]> = {};
    edges.forEach((edge: any) => {
      if (!adj[edge.source]) adj[edge.source] = [];
      if (!adj[edge.target]) adj[edge.target] = [];
      adj[edge.source].push(edge.target);
      adj[edge.target].push(edge.source); // Bidirectional
    });
    return adj;
  }, [edges]);

  // BFS path finding
  const findPath = useCallback((start: string, end: string): string[] => {
    if (start === end) return [start];

    const visited = new Set<string>();
    const queue: { node: string; path: string[] }[] = [{ node: start, path: [start] }];

    while (queue.length > 0) {
      const { node, path } = queue.shift()!;

      if (node === end) return path;

      if (visited.has(node)) continue;
      visited.add(node);

      const neighbors = adjacencyList[node] || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          queue.push({ node: neighbor, path: [...path, neighbor] });
        }
      }
    }

    return []; // No path found
  }, [adjacencyList]);

  // Handle path finding when both nodes are selected
  useEffect(() => {
    if (pathStart && pathEnd) {
      const path = findPath(pathStart, pathEnd);
      setPathNodes(path);
      if (path.length > 0) {
        setSelectedNodes(path);
      }
    } else {
      setPathNodes([]);
    }
  }, [pathStart, pathEnd, findPath]);

  // Handle node click
  const handleNodeClick = useCallback((node: any) => {
    if (pathFindingMode) {
      // Path finding mode: select start and end nodes
      if (!pathStart) {
        setPathStart(node.id);
        setSelectedNodes([node.id]);
      } else if (!pathEnd && node.id !== pathStart) {
        setPathEnd(node.id);
      } else {
        // Reset and start new path
        setPathStart(node.id);
        setPathEnd(null);
        setPathNodes([]);
        setSelectedNodes([node.id]);
      }
    } else {
      // Normal mode: single selection
      setSelectedNodes([node.id]);
      selectNode({
        id: node.id,
        label: node.label || node.id,
        type: node.data?.type || 'unknown',
        status: node.data?.status,
        properties: node.data,
      }, projectId);
    }
  }, [pathFindingMode, pathStart, pathEnd, selectNode, projectId]);

  // Handle node hover
  const handleNodePointerOver = useCallback((node: any) => {
    setActiveNodes([node.id]);
  }, []);

  const handleNodePointerOut = useCallback(() => {
    setActiveNodes([]);
  }, []);

  // Handle right-click context menu
  const handleNodeContextMenu = useCallback((node: any, event: any) => {
    event.preventDefault?.();
    setContextMenu({
      x: event.clientX || event.x || 100,
      y: event.clientY || event.y || 100,
      node,
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Context menu actions
  const handleViewDetails = useCallback(() => {
    if (contextMenu?.node) {
      selectNode({
        id: contextMenu.node.id,
        label: contextMenu.node.label || contextMenu.node.id,
        type: contextMenu.node.data?.type || 'unknown',
        status: contextMenu.node.data?.status,
        properties: contextMenu.node.data,
      }, projectId);
    }
    closeContextMenu();
  }, [contextMenu, selectNode, projectId, closeContextMenu]);

  const handleCopyId = useCallback(() => {
    if (contextMenu?.node) {
      navigator.clipboard.writeText(contextMenu.node.id);
    }
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  const handleStartPath = useCallback(() => {
    if (contextMenu?.node) {
      setPathFindingMode(true);
      setPathStart(contextMenu.node.id);
      setPathEnd(null);
      setPathNodes([]);
      setSelectedNodes([contextMenu.node.id]);
    }
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    graphRef.current?.zoomIn?.();
  }, []);

  const handleZoomOut = useCallback(() => {
    graphRef.current?.zoomOut?.();
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.fitNodesInView?.();
  }, []);

  // Reset path finding
  const resetPathFinding = useCallback(() => {
    setPathFindingMode(false);
    setPathStart(null);
    setPathEnd(null);
    setPathNodes([]);
    setSelectedNodes([]);
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => closeContextMenu();
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, closeContextMenu]);

  if (!isClient || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading project graph...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>Failed to load graph</p>
        <p className="text-sm text-muted-foreground mt-2">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No graph data available</p>
        <p className="text-sm mt-2">This project has no RFQs or POs yet</p>
      </div>
    );
  }

  if (!GraphCanvas) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph renderer...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">{projectName || 'Project Graph'}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Clustering toggle */}
          <button
            onClick={() => setClusteringEnabled(!clusteringEnabled)}
            className={`p-1.5 rounded text-xs transition-colors ${
              clusteringEnabled
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Toggle clustering"
          >
            Cluster
          </button>

          {/* Path finding toggle */}
          <button
            onClick={() => pathFindingMode ? resetPathFinding() : setPathFindingMode(true)}
            className={`p-1.5 rounded transition-colors ${
              pathFindingMode
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Find path between nodes"
          >
            <Route className="w-4 h-4" />
          </button>

          {/* Zoom controls */}
          <div className="flex items-center border-l border-panel-border pl-2 ml-1">
            <button
              onClick={handleZoomIn}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomOut}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={handleFitView}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Fit to view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground border-l border-panel-border pl-2 ml-1">
            <span>{data?.metadata.node_count} nodes</span>
            <span>{data?.metadata.edge_count} edges</span>
          </div>
        </div>
      </div>

      {/* Path finding status bar */}
      {pathFindingMode && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border-b border-primary/20 text-sm">
          <Route className="w-4 h-4 text-primary" />
          <span>
            {!pathStart && 'Click a node to set start point'}
            {pathStart && !pathEnd && 'Click another node to find path'}
            {pathStart && pathEnd && pathNodes.length > 0 && (
              <span className="text-primary font-medium">
                Path found: {pathNodes.length} nodes
              </span>
            )}
            {pathStart && pathEnd && pathNodes.length === 0 && (
              <span className="text-destructive">No path found</span>
            )}
          </span>
          <button
            onClick={resetPathFinding}
            className="ml-auto p-1 rounded hover:bg-primary/20 transition-colors"
            title="Exit path finding"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Graph Container */}
      <div
        className="flex-1 bg-background relative overflow-hidden"
        style={{ minHeight: '400px' }}
        suppressHydrationWarning
      >
        <div className="absolute inset-0">
          <GraphCanvas
            ref={graphRef}
            nodes={nodes}
            edges={edges}
            layoutType="forceDirected2d"
            clusterAttribute={clusteringEnabled ? 'cluster' : undefined}
            labelType="nodes"
            draggable
            animated
            cameraMode="pan"
            edgeArrowPosition="end"
            edgeLabelPosition="above"
            theme={themes ? (resolvedTheme === 'dark' ? themes.dark : themes.light) : undefined}
            selections={selectedNodes}
            actives={activeNodes}
            onNodeClick={handleNodeClick}
            onNodePointerOver={handleNodePointerOver}
            onNodePointerOut={handleNodePointerOut}
            onNodeContextMenu={handleNodeContextMenu}
          />
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <div
            className="absolute z-50 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[160px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border mb-1">
              {contextMenu.node.data?.type}: {contextMenu.node.label}
            </div>
            <button
              onClick={handleViewDetails}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
            <button
              onClick={handleStartPath}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <Route className="w-4 h-4" />
              Find Path From Here
            </button>
            <button
              onClick={handleCopyId}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Node ID
            </button>
            {contextMenu.node.data?.odoo_id && (
              <button
                onClick={() => {
                  window.open(`/odoo/${contextMenu.node.data.type}/${contextMenu.node.data.odoo_id}`, '_blank');
                  closeContextMenu();
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open in Odoo
              </button>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-panel-border bg-panel text-xs">
        <div className="flex items-center gap-4">
          {Object.entries(NODE_COLORS).slice(0, 6).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-muted-foreground capitalize">{type.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
        <div className="text-muted-foreground">
          <MousePointer2 className="w-3 h-3 inline mr-1" />
          Click to select • Right-click for menu
        </div>
      </div>
    </div>
  );
}
